import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import AdminLayout from '../components/AdminLayout';
import './AdminDashboard.css';
import './AdminAuditLog.css';

// Read-only record of settings, staff, export and bulk changes (super-admins only; enforced by row-level security).
// Application status changes are in each application's History tab.
export const ENTITIES = {
  admission_settings: 'Admission controls',
  school_settings: 'School settings',
  grades: 'Grade eligibility',
  admin_users: 'Staff accounts',
  admissions: 'Applications',
  contact_inquiries: 'Enquiries',
  email_notifications: 'Email notifications',
  gallery_albums: 'Gallery'
};
const ACTIONS = {
  update: 'Changed', create: 'Created', deactivate: 'Deactivated', reactivate: 'Reactivated', change_role: 'Changed role',
  update_status: 'Changed status', bulk_status_update: 'Bulk status change', set_interview_date: 'Set interview date',
  export: 'Exported', export_sensitive: 'Exported medical answers', print_sensitive: 'Printed medical answers', retry: 'Retried',
  delete: 'Deleted', publish: 'Published', unpublish: 'Unpublished', add_photo: 'Added photo', remove_photo: 'Removed photo'
};
const PAGE_SIZE = 100;
const MAX_VALUE = 80;

const show = (value) => {
  if (value === null || value === undefined || value === '') return '(empty)';
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  return text.length > MAX_VALUE ? `${text.slice(0, MAX_VALUE)}…` : text;
};

// The fields that differ between the old and new values (or all new values when there is no "before").
export const changedFields = (oldValues, newValues) => {
  const before = oldValues ?? {};
  const after = newValues ?? {};
  return Object.keys(after)
    .filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]))
    .map((k) => ({ field: k, from: oldValues ? show(before[k]) : null, to: show(after[k]) }));
};

const AdminAuditLog = () => {
  const [rows, setRows] = useState([]);
  const [entity, setEntity] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    let query = supabase
      .from('audit_log')
      .select('id, action, entity, entity_id, old_values, new_values, created_at, actor:admin_users(full_name)')
      .order('created_at', { ascending: false })
      .limit(PAGE_SIZE);
    if (entity) query = query.eq('entity', entity);
    const { data, error: queryError } = await query;
    if (queryError) {
      console.error('Failed to load the audit log', queryError);
      setError('Failed to load the audit log');
      setRows([]);
    } else {
      setRows(data ?? []);
    }
    setLoading(false);
  }, [entity]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AdminLayout>
      <div className="admin-dashboard admin-audit">
        <div className="dashboard-header">
          <div>
            <h1>Audit Log</h1>
            <p>Who changed settings, staff accounts, grade eligibility and the gallery, bulk updates and exports, newest first.</p>
          </div>
        </div>

        <div className="audit-filters">
          <label htmlFor="audit-entity">Area</label>
          <select id="audit-entity" value={entity} onChange={(e) => setEntity(e.target.value)}>
            <option value="">All</option>
            {Object.entries(ENTITIES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>

        {loading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading the audit log...</p>
          </div>
        )}
        {error && <div className="error-banner" role="alert">{error}</div>}

        {!loading && !error && (rows.length === 0 ? (
          <p className="no-data">No recorded changes{entity ? ' in this area' : ''} yet.</p>
        ) : (
          <div className="applications-table-container">
            <table className="applications-table">
              <thead>
                <tr><th>When</th><th>Who</th><th>What</th><th>Details</th></tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const changes = changedFields(row.old_values, row.new_values);
                  return (
                    <tr key={row.id}>
                      <td>{new Date(row.created_at).toLocaleString()}</td>
                      <td>{row.actor?.full_name || 'System'}</td>
                      <td>
                        {ACTIONS[row.action] ?? row.action} · {ENTITIES[row.entity] ?? row.entity}
                        {row.entity_id && <span className="audit-id"> #{row.entity_id}</span>}
                      </td>
                      <td className="audit-details">
                        {changes.length === 0 ? '—' : (
                          <ul>
                            {changes.map((c) => (
                              <li key={c.field}>
                                <strong>{c.field}</strong>: {c.from !== null ? <>{c.from} → </> : null}{c.to}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.length === PAGE_SIZE && <p className="help-text">Showing the newest {PAGE_SIZE}. Choose an area to narrow the list.</p>}
          </div>
        ))}
      </div>
    </AdminLayout>
  );
};

export default AdminAuditLog;
